/**
 * Tracks per-investigation object-set contributions client-side.
 *
 * Each contribution corresponds to one "Add to object set" action — an
 * insight resolution, an agent tool result, or a neighbour-expansion.
 * Storing the source label + the node/edge ids the contribution introduced
 * lets us:
 *
 *  • Render a chip per contribution as a floating bar on the lens views.
 *  • Toggle a chip off → temporarily hide its nodes/edges from the canvas
 *    without removing them from the underlying investigation.
 *  • Drop a chip → remove its nodes/edges from the active investigation
 *    (writes back via the investigations store).
 *
 * State is in memory only — when the user reloads the page, contribution
 * chips reset. The persisted artefact is the investigation's graphView; the
 * chips are an ergonomic layer on top of it.
 */

import { useMemo } from 'react'
import { create } from 'zustand'

export type ContributionSource = 'insight' | 'agent' | 'expand'

export interface ObjectSetContribution {
  id: string
  /** Investigation id (or `'transient'` for the transient slot) the
   *  contribution belongs to. Lets a single store hold contributions for
   *  multiple investigations the user has touched in the session. */
  investigationKey: string
  /** Short, human-readable label used as the chip text. */
  label: string
  source: ContributionSource
  nodeIds: string[]
  edgeIds: string[]
  /** When false, views should hide these node/edge ids. */
  enabled: boolean
  createdAt: number
}

interface ContributionsState {
  byKey: Record<string, ObjectSetContribution[]>

  add(contribution: Omit<ObjectSetContribution, 'enabled' | 'createdAt'>): void
  toggle(investigationKey: string, contributionId: string): void
  remove(investigationKey: string, contributionId: string): void
  clear(investigationKey: string): void
}

export const useObjectSetContributionsStore = create<ContributionsState>()(set => ({
  byKey: {},

  add(contribution) {
    set(state => {
      const list = state.byKey[contribution.investigationKey] ?? []
      const next: ObjectSetContribution = {
        ...contribution,
        enabled: true,
        createdAt: Date.now(),
      }
      return {
        byKey: { ...state.byKey, [contribution.investigationKey]: [...list, next] },
      }
    })
  },

  toggle(investigationKey, contributionId) {
    set(state => {
      const list = state.byKey[investigationKey] ?? []
      const updated = list.map(c => (c.id === contributionId ? { ...c, enabled: !c.enabled } : c))
      return { byKey: { ...state.byKey, [investigationKey]: updated } }
    })
  },

  remove(investigationKey, contributionId) {
    set(state => {
      const list = state.byKey[investigationKey] ?? []
      return {
        byKey: {
          ...state.byKey,
          [investigationKey]: list.filter(c => c.id !== contributionId),
        },
      }
    })
  },

  clear(investigationKey) {
    set(state => {
      if (!state.byKey[investigationKey]) return state
      const next = { ...state.byKey }
      delete next[investigationKey]
      return { byKey: next }
    })
  },
}))

/**
 * Returns the contribution list for the currently-relevant viewpoint.
 *
 * IMPORTANT: returns a stable empty array when the key has no entries —
 * a fresh `[]` every call would invalidate Zustand's snapshot equality and
 * push every consumer into an infinite render loop.
 */
const EMPTY_LIST: readonly ObjectSetContribution[] = []
export function useContributionsFor(key: string | undefined): readonly ObjectSetContribution[] {
  return useObjectSetContributionsStore(s => (key ? (s.byKey[key] ?? EMPTY_LIST) : EMPTY_LIST))
}

/**
 * Returns the union of node ids hidden by disabled contributions for the
 * given key. Lens views can subtract this set from the visible nodes.
 *
 * Implemented as a list-selector + `useMemo` so the returned Set has stable
 * identity across renders when the underlying contribution list hasn't
 * changed — selectors that compute fresh objects on every call defeat
 * Zustand's snapshot caching and cause "Maximum update depth exceeded".
 */
export function useHiddenNodeIds(key: string | undefined): ReadonlySet<string> {
  const list = useContributionsFor(key)
  return useMemo(() => {
    if (list.length === 0) return EMPTY_SET
    const out = new Set<string>()
    for (const c of list) {
      if (c.enabled) continue
      for (const id of c.nodeIds) out.add(id)
    }
    return out.size === 0 ? EMPTY_SET : out
  }, [list])
}

const EMPTY_SET: ReadonlySet<string> = new Set()
